import setAuctionType from '@/api/SetAuctionType';
import offerStates from '@/constants/offerStates';
import store from '@/store';
import EasyPassClient from './EasyPassClient';

class MyAcvClient {
  constructor(user) {
    this.rootUri = 'myacv/auctions';
    this.apiVersion = 'v2';
    this.easyPassClient = new EasyPassClient(user);
    this.bffEasyPassRootUri = 'bff/myacv';
    this.myAcvEasyPassRootUri = 'api/acvapi/v3';
    this.easyPassHeaders = {
      'json-naming-strategy': 'camel',
    };
  }

  async getMyAcvActiveBuyingAuctions(params) {
    const useBff = await store.dispatch(
      'getFlag',
      store.state.featureFlags.featureFlagSlugs.webMyAcvActiveBuyingUseBff,
    );

    if (useBff) {
      return this.easyPassClient.get(
        `${this.bffEasyPassRootUri}/active/buying`,
        params,
      );
    }

    return this.easyPassClient.get(`${this.myAcvEasyPassRootUri}/${this.rootUri}/buying/active`, params)
      .then((response) => setAuctionType(response, 'buying'));
  }

  getMyAcvPendingProxyAuctions(params) {
    return this.easyPassClient.get(`${this.myAcvEasyPassRootUri}/${this.rootUri}/buying/persistent_proxy`, params)
      .then((response) => setAuctionType(response, 'pendingProxy'));
  }

  async getMyAcvActiveSellingAuctions(params) {
    // the feature flag cards are the only one which can use camelCase
    const auctionListV2 = await store.dispatch(
      'getFlag',
      store.state.featureFlags.featureFlagSlugs.webMyAcvAuctionListV2,
    );

    return this.easyPassClient.get(
      `${this.bffEasyPassRootUri}/active/selling`,
      params,
      auctionListV2 ? this.easyPassHeaders : null,
    );
  }

  getMyAcvWonAuctions(params) {
    return this.easyPassClient.get(`${this.myAcvEasyPassRootUri}/${this.rootUri}/buying/won`, params)
      .then((response) => setAuctionType(response, 'buying'));
  }

  getMyAcvWonAuctionsWithTitleInfo(params) {
    return this.easyPassClient.get(`${this.bffEasyPassRootUri}/auctions/buying/won`, params)
      .then((response) => setAuctionType(response, 'buying'));
  }

  getMyAcvSoldAuctions(params) {
    return this.easyPassClient.get(`${this.myAcvEasyPassRootUri}/${this.rootUri}/selling/sold`, params)
      .then((response) => setAuctionType(response, 'selling'));
  }

  async getMyAcvOffers(params) {
    const useBff = await store.dispatch(
      'getFlag',
      store.state.featureFlags.featureFlagSlugs.showEstimateOfferDashboard,
    );

    const url = useBff ? 'bff/offers' : 'api/acvapi/v2/offers';

    return this.easyPassClient.get(url, params);
  }

  getMyAcvDeals(params) {
    return this.easyPassClient.get('bff/deals', params);
  }

  getMyAcvOffersDeal(dealId) {
    return this.easyPassClient.get(`bff/deals/${dealId}`);
  }

  async putMyAcvOffers(offerId, action, notAvailable = false, minAmount = null, rejectionReason = 'Rejected by user') {
    const useBff = await store.dispatch(
      'getFlag',
      store.state.featureFlags.featureFlagSlugs.showNegotiationsOnMyAcvOffersTab,
    );
    let data = {};

    if (action === 'accept') {
      data = {
        offer_state: offerStates.ACCEPTED,
        offer_value: minAmount,
      };
    } else if (action === 'reject') {
      data = {
        offer_state: offerStates.REJECTED,
        rejection_reason: rejectionReason,
        no_longer_available: notAvailable,
        min_amonut: minAmount,
      };
    } else if (action === 'counter' || action === 'make-offer') {
      data = {
        offer_state: offerStates.PENDING,
        offer_value: minAmount,
      };
      if (!this.easyPassClient.user.hasMultipleDealerships) {
        data.dealer_id = this.easyPassClient.user.dealer_id;
      }
    } else {
      throw new Error('Invalid action');
    }
    const url = useBff ? `bff/deals/${offerId}` : `api/acvapi/v2/offers/${offerId}`;
    return this.easyPassClient
      .put(url, data)
      .then((response) => response);
  }

  async getMyAcvNegotiationsBuyingAuctions(params) {
    const useBff = await store.dispatch(
      'getFlag',
      store.state.featureFlags.featureFlagSlugs.useBffEndpointForNegotiationBuying,
    );

    const url = useBff ? `${this.bffEasyPassRootUri}/negotiation/buying`
      : `${this.myAcvEasyPassRootUri}/${this.rootUri}/buying/negotiation`;

    return this.easyPassClient.get(url, params)
      .then((response) => setAuctionType(response, 'offers'));
  }

  async getMyAcvNegotiationsSellingAuctions(params) {
    const useBff = await store.dispatch(
      'getFlag',
      store.state.featureFlags.featureFlagSlugs.useBffEndpointForNegotiationSelling,
    );

    const url = useBff ? `${this.bffEasyPassRootUri}/negotiation/selling`
      : `${this.myAcvEasyPassRootUri}/${this.rootUri}/selling/negotiation`;

    return this.easyPassClient.get(url, params)
      .then((response) => setAuctionType(response, 'offers'));
  }

  getMyAcvWonExportFile(params) {
    return this.easyPassClient.get(`${this.myAcvEasyPassRootUri}/${this.rootUri}/buying/won/export`, params, null);
  }

  getMyAcvSoldExportFile(params) {
    return this.easyPassClient.get(`${this.myAcvEasyPassRootUri}/${this.rootUri}/selling/sold/export`, params, null);
  }

  getMyAcvUnsoldExportFile(params) {
    return this.easyPassClient.get(`${this.myAcvEasyPassRootUri}/${this.rootUri}/selling/unsold/export`, params, null);
  }

  getMyAcvSavedExportFile(params) {
    return this.easyPassClient.get(`${this.myAcvEasyPassRootUri}/${this.rootUri}/selling/saved/export`, params, null);
  }

  getMyAcvInProcessExportFile(params) {
    return this.easyPassClient.get(`${this.myAcvEasyPassRootUri}/myacv/titles/export/inprocess`, params, null);
  }

  getMyAcvFinalizedExportFile(params) {
    return this.easyPassClient.get(`${this.myAcvEasyPassRootUri}/myacv/titles/export/finalized`, params, null);
  }

  async downloadMyAcvFinalizedDocument(params, auctionId, docType) {
    const endpointName = docType === 'dealership license' ? 'dealer_license' : 'resale_certificate';
    const finalUrl = `${this.myAcvEasyPassRootUri}/myacv/titles/finalized/${auctionId}/${endpointName}`;
    const response = await this.easyPassClient.downloadFile(finalUrl);
    const fileName = response.headers['content-disposition']?.split('=')[1];
    const contentType = response.headers['content-type'];
    const blob = new Blob([response.data], { type: contentType });
    if (window.navigator && window.navigator.msSaveOrOpenBlob) {
      window.navigator.msSaveOrOpenBlob(blob, fileName);
    } else {
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = fileName;
      link.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true, view: window }));
    }
  }
}

export default MyAcvClient;
